import { createChart } from 'lightweight-charts';
import { formatters } from 'common';
import { API } from '@xbto/api-client';
import { defaultMarkerSeriesOptions, } from './marker-series/options';
import { chartOptions, fundGraphChartHeight } from './options';
import { getLeftYAxisLabel, getRightYAxisLabel, getXAxisLabel } from './utils';
const _makeTooltips = container => {
    const commonTooltipStyle = `height: auto; position: absolute; box-sizing: border-box; text-align: left; pointer-events: none; font-family: Inter, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; transition: 0.15s;`;
    const yAxisTooltipStyle = `${commonTooltipStyle} z-index: 2; padding: 8px; font-size: 10px; background: #000; color: #fff; transform: translate(0%, -50%); display: none;`;
    const xAxisTooltipStyle = `${commonTooltipStyle} z-index: 3; padding: 8px; font-size: 10px; background: #000; color: #fff; display: none; text-align: center; bottom: 0; transform: translate(-50%, 0%);`;
    const plotAreaTooltip = document.createElement('div');
    plotAreaTooltip.style = `${commonTooltipStyle} z-index: 4; padding: 12px; font-size: 12px; border-radius: 4px; box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.12); width: 248px; display: none; transform: translate(-50%, -110%); background: #fff; color: #181428`;
    container.appendChild(plotAreaTooltip);
    const yAxisLeftTooltip = document.createElement('div');
    yAxisLeftTooltip.style = yAxisTooltipStyle;
    container.appendChild(yAxisLeftTooltip);
    const yAxisRightTooltip = document.createElement('div');
    yAxisRightTooltip.style = yAxisTooltipStyle;
    container.appendChild(yAxisRightTooltip);
    const xAxisTooltip = document.createElement('div');
    xAxisTooltip.style = xAxisTooltipStyle;
    container.appendChild(xAxisTooltip);
    return { plotAreaTooltip, yAxisLeftTooltip, yAxisRightTooltip, xAxisTooltip };
};
const _getMinMaxFromValuesAndSteps = (dataPoints) => {
    const values = [];
    for (const dP of dataPoints) {
        values.push(dP.value);
        if (dP.steps && dP.steps.length > 0) {
            values.push(...dP.steps);
        }
    }
    return { min: Math.min(...values), max: Math.max(...values) };
};
const _managePlotAreaTooltip = (param, tooltip, chart, isAsset, dataPoints, seriesAssetAmount, seriesUsdValue) => {
    var _a;
    if (!tooltip || !chart) {
        return;
    }
    const dataPoint = isAsset ? dataPoints.assetAmount : dataPoints.usdValue;
    if (!dataPoint ||
        !dataPoint.marker ||
        !dataPoint.marker.totalInvestedInKind ||
        !dataPoint.marker.totalInvestedInUsd ||
        !dataPoint.marker.asset) {
        return;
    }
    const series = isAsset ? seriesAssetAmount : seriesUsdValue;
    if (!series) {
        return;
    }
    const seriesData = param.seriesData.get(series);
    if (!seriesData) {
        return;
    }
    const coordinateY = series.priceToCoordinate(seriesData.value);
    const coordinateX = chart.timeScale().timeToCoordinate(seriesData.time);
    if (coordinateY === null) {
        return;
    }
    const type = dataPoint.marker.markerOperation === API.MarkerOperation.Subscription
        ? `Subscribe`
        : `Redeem`;
    const description = dataPoint.marker.markerOperation === API.MarkerOperation.Subscription
        ? 'Amount invested'
        : 'Amount redeemed';
    const month = formatters.getDate(new Date(param.time * 1000), 'dd/MM/yyyy', true);
    const total = isAsset
        ? formatters.getCurrency(dataPoint.marker.totalInvestedInKind, dataPoint.marker.secondaryAssetCode || '')
        : formatters.getBaseCurrency(dataPoint.marker.totalInvestedInUsd);
    const num = formatters.getAmount(dataPoint.marker.shares) || '-';
    tooltip.style.display = 'block';
    tooltip.innerHTML = `
      <div style="display: flex; justify-content: space-between; font-size: 14px;">
        <span style="font-weight: 500;">${type}</span>
        <span style="color: #676767;  text-align: right;">${month}</span>
      </div>
      <div style="margin-top: 12px; display: flex; justify-content: space-between;">
        <span style="color: #676767;">${description}</span>
        <span style="color: #181428; text-align: right;">${total}</span>
      </div>
      <div style="margin-top: 8px; display: flex; justify-content: space-between;">
      <span style="color: #676767;"># of shares</span>
        <span style="color: #181428; text-align: right;">${num}</span>
      </div>
      `;
    const priceScaleWidth = seriesUsdValue
        ? (_a = series === null || series === void 0 ? void 0 : series.priceScale('left')) === null || _a === void 0 ? void 0 : _a.width()
        : 0;
    const paneSize = chart.paneSize();
    const plotAreaTooltipCoordinateY = coordinateY > 0 ? coordinateY : Math.max(0, fundGraphChartHeight);
    const plotAreaTooltipCoordinateX = coordinateX > 0
        ? coordinateX + priceScaleWidth
        : Math.max(0, Math.min(paneSize.width));
    tooltip.style.top = plotAreaTooltipCoordinateY + 'px';
    const tooltipAllowedOffset = tooltip.clientWidth / 2;
    const centerAllowedOffset = {
        min: paneSize.width * 0.4,
        max: paneSize.width * 0.6,
    };
    const isCloseToLeftBoundary = plotAreaTooltipCoordinateX < tooltipAllowedOffset;
    const isCloseToRightBoundary = Math.abs(plotAreaTooltipCoordinateX - paneSize.width) <=
        tooltipAllowedOffset;
    const isNearCenter = plotAreaTooltipCoordinateX >= centerAllowedOffset.min &&
        plotAreaTooltipCoordinateX <= centerAllowedOffset.max;
    if (isNearCenter) {
        tooltip.style.left = plotAreaTooltipCoordinateX + 'px';
        return;
    }
    if (isCloseToLeftBoundary && isCloseToRightBoundary) {
        /**
         * Note:
         * Truly an edge case, the viewport has to be smaller than 248px here
         * Intentionally doing nothing today & leaving to default, as I am not sure what we will be doing on a viewport of 248px
         * but may be can implement scaling transform on a later date
         */
        tooltip.style.left = plotAreaTooltipCoordinateX + 'px';
        return;
    }
    if (isCloseToLeftBoundary) {
        tooltip.style.left = tooltipAllowedOffset + priceScaleWidth + 'px';
        return;
    }
    if (isCloseToRightBoundary) {
        tooltip.style.left = plotAreaTooltipCoordinateX - priceScaleWidth + 'px';
        return;
    }
    // Note: when !isCloseToLeftBoundary && !isCloseToRightBoundary - there is enough space on both side - center align
    tooltip.style.left = plotAreaTooltipCoordinateX + 'px';
};
const _manageYAxisTooltip = (param, tooltip, series, getStyle, getLabel) => {
    if (!tooltip) {
        return;
    }
    if (!series) {
        return;
    }
    const seriesData = param.seriesData.get(series);
    if (!seriesData) {
        return;
    }
    const coordinate = series.priceToCoordinate(seriesData.value);
    if (coordinate === null) {
        return;
    }
    const tooltipCoordinateY = coordinate > 0 ? coordinate : Math.max(0, fundGraphChartHeight);
    tooltip.style.display = 'block';
    const label = getLabel(seriesData.value);
    tooltip.innerHTML = `<div>${label}</div>`;
    const { left, right } = getStyle();
    tooltip.style.left = left;
    tooltip.style.right = right;
    tooltip.style.top = tooltipCoordinateY + 'px';
};
const _manageXAxisTooltip = (param, tooltip, chart, series, seriesUsdValue) => {
    var _a;
    if (!tooltip || !param.time) {
        return;
    }
    tooltip.style.display = 'block';
    const coordinateX = chart.timeScale().timeToCoordinate(param.time);
    const priceScaleWidth = seriesUsdValue
        ? (_a = series === null || series === void 0 ? void 0 : series.priceScale('left')) === null || _a === void 0 ? void 0 : _a.width()
        : 0;
    const label = getXAxisLabel(param.time);
    tooltip.innerHTML = `<div>${label}</div>`;
    tooltip.style.left = coordinateX + priceScaleWidth + 'px';
};
const _getLineSeriesHitTest = (x, y, primitives) => {
    let lineAssetSeriesHit = false;
    let lineUsdSeriesHit = false;
    const lineAssetSeries = primitives.lineAssetSeries;
    if (lineAssetSeries) {
        lineAssetSeriesHit = lineAssetSeries.isHit(lineAssetSeries.path, x, y);
    }
    const lineUsdSeries = primitives.lineUsdSeries;
    if (lineUsdSeries) {
        lineUsdSeriesHit = lineUsdSeries.isHit(lineUsdSeries.path, x, y);
    }
    return { lineAssetSeriesHit, lineUsdSeriesHit };
};
class ChartManager {
    constructor() {
        this._data = null;
        this._secondaryAsset = null;
        this._selectedSeries = null;
        this._container = null;
        this._chart = null;
        this._series = {};
        this._tooltips = {};
        this._crosshairSubscribed = false;
        this._primitives = {};
        this._onSeriesLineSelected = null;
        this._innerHandleCrosshairMove = param => {
            if (!this._container ||
                !Object.keys(this._tooltips).length ||
                !this._data ||
                !this._selectedSeries) {
                return;
            }
            if (!this._crosshairSubscribed) {
                this._crosshairSubscribed = true;
            }
            const plotAreaTooltip = this.getTooltip('plot-area-tooltip');
            const yAxisLeftTooltip = this.getTooltip('y-axis-left-tooltip');
            const yAxisRightTooltip = this.getTooltip('y-axis-right-tooltip');
            const xAxisTooltip = this.getTooltip('x-axis-tooltip');
            if (param.point === undefined ||
                !param.time ||
                param.logical === undefined ||
                param.point.x < 0 ||
                param.point.x > this._container.clientWidth ||
                param.point.y < 0 ||
                param.point.y > this._container.clientHeight) {
                if (plotAreaTooltip) {
                    plotAreaTooltip.style.display = 'none';
                }
                if (yAxisLeftTooltip) {
                    yAxisLeftTooltip.style.display = 'none';
                }
                if (yAxisRightTooltip) {
                    yAxisRightTooltip.style.display = 'none';
                }
                if (xAxisTooltip) {
                    xAxisTooltip.style.display = 'none';
                }
                return;
            }
            const { lineAssetSeriesHit, lineUsdSeriesHit } = _getLineSeriesHitTest(param.point.x, param.point.y, this._primitives);
            if (lineAssetSeriesHit || lineUsdSeriesHit) {
                this._container.style.cursor = 'pointer';
            }
            else {
                this._container.style.cursor = 'auto';
            }
            const dataPointIndex = param.logical;
            if (dataPointIndex < 0) {
                return;
            }
            const dataPoints = {
                assetAmount: this._data.assetAmountDataPoints[dataPointIndex],
                usdValue: this._data.usdValueDataPoints[dataPointIndex],
            };
            const isAsset = this._selectedSeries === 'asset-amount';
            const seriesAssetAmount = this.getSeries('lineAssetSeries');
            const seriesUsdValue = this.getSeries('lineUsdSeries');
            _managePlotAreaTooltip(param, plotAreaTooltip, this._chart, isAsset, dataPoints, seriesAssetAmount, seriesUsdValue);
            if (seriesUsdValue) {
                _manageYAxisTooltip(param, yAxisLeftTooltip, seriesAssetAmount, () => {
                    return {
                        left: `0px`,
                        right: `auto`,
                    };
                }, v => getLeftYAxisLabel(v, this._secondaryAsset, false));
            }
            _manageYAxisTooltip(param, yAxisRightTooltip, !seriesUsdValue ? seriesAssetAmount : seriesUsdValue, () => {
                return {
                    left: `auto`,
                    right: `0px`,
                };
            }, v => getRightYAxisLabel(v, false));
            _manageXAxisTooltip(param, xAxisTooltip, this._chart, seriesAssetAmount, seriesUsdValue);
        };
        this._handleCrosshairMove = param => {
            this._innerHandleCrosshairMove(param);
        };
        this._handleClick = param => {
            if (!this._onSeriesLineSelected) {
                return;
            }
            if (!param || !param.point || !param.point.x || !param.point.y) {
                return;
            }
            const { lineAssetSeriesHit, lineUsdSeriesHit } = _getLineSeriesHitTest(param.point.x, param.point.y, this._primitives);
            if (lineAssetSeriesHit) {
                this._onSeriesLineSelected('asset-amount');
            }
            else if (lineUsdSeriesHit) {
                this._onSeriesLineSelected('usd-value');
            }
        };
        this._fitChart = () => {
            if (!this._chart || !this._container || !this._data) {
                return;
            }
            this._chart.applyOptions({ width: this._container.clientWidth });
            const timeScale = this._chart.timeScale();
            if (!timeScale) {
                return;
            }
            timeScale.fitContent();
            const dataPointsCount = this._data.assetAmountDataPoints.length; // Note: usd value datapoints are optional/ nullable for usd graph so it is enough we compute on the asset amount datapoints
            if (dataPointsCount <= 0) {
                // Note: Should never be the case, but better handled than sorry
                return;
            }
            const minVisibleCount = 9; // Note: design requirement - We try to plot or give space to minimum of 9 data points/ bar widths/ visible range, to allow for whitespace to right to less number of data points
            const numOfDataPointsToAdd = dataPointsCount < minVisibleCount ? minVisibleCount - dataPointsCount : 0;
            if (numOfDataPointsToAdd > 0) {
                timeScale.setVisibleLogicalRange({
                    from: 0.25,
                    // Note: this is adding white space to the right of the few data points availanle for design/ product requirement / satisfaction
                    to: dataPointsCount + numOfDataPointsToAdd - 0.25,
                });
                return;
            }
            // Note: Below math is to lose the margins on the left and right of the chart container by narrowing the visible logical range
            timeScale.setVisibleLogicalRange({
                from: 0.25,
                to: dataPointsCount - 0.25,
            });
        };
        this._onResize = () => this._fitChart();
    }
    setDataAndSeries(data, secondaryAsset, selectedSeries) {
        this._data = data;
        this._secondaryAsset = secondaryAsset;
        this._selectedSeries = selectedSeries;
    }
    reDraw(data, secondaryAsset, selectedSeries, chartColors) {
        var _a, _b;
        this.setDataAndSeries(data, secondaryAsset, selectedSeries);
        const chart = this.getChart();
        const lineAssetSeries = this.getSeries('lineAssetSeries');
        const markerAssetSeries = this.getSeries('markerAssetSeries');
        const lineUsdSeries = this.getSeries('lineUsdSeries');
        const markerUsdSeries = this.getSeries('markerUsdSeries');
        if (!chart || !lineAssetSeries || !markerAssetSeries) {
            return;
        }
        const { min: assetAmountMin, max: assetAmountMax } = _getMinMaxFromValuesAndSteps(data.assetAmountDataPoints);
        lineAssetSeries.setData(data.assetAmountDataPoints);
        lineAssetSeries.applyOptions({
            color: chartColors.assetAmount,
            autoscaleInfoProvider: () => ({
                priceRange: {
                    minValue: assetAmountMin,
                    maxValue: assetAmountMax,
                },
            }),
        });
        if (lineUsdSeries) {
            const { min: usdValueMin, max: usdValueMax } = _getMinMaxFromValuesAndSteps(data.usdValueDataPoints);
            lineUsdSeries.setData(data.usdValueDataPoints);
            lineUsdSeries.applyOptions({
                color: chartColors.usdValue,
                autoscaleInfoProvider: () => ({
                    priceRange: {
                        minValue: usdValueMin,
                        maxValue: usdValueMax,
                    },
                }),
            });
        }
        if (selectedSeries === 'asset-amount') {
            markerAssetSeries.applyOptions(Object.assign(Object.assign({}, defaultMarkerSeriesOptions), { color: chartColors.assetAmount, hide: false }));
            markerAssetSeries.setData(data.assetAmountMarkersDataPoints);
            if (markerUsdSeries) {
                markerUsdSeries.applyOptions({
                    hide: true,
                    // TODO:@jey - type options object in IseriesApi so we do not need to typecast here
                });
            }
        }
        if (selectedSeries === 'usd-value') {
            markerAssetSeries.applyOptions({
                hide: true,
                // TODO:@jey - type options object in IseriesApi so we do not need to typecast here
            });
            if (markerUsdSeries) {
                markerUsdSeries.applyOptions(Object.assign(Object.assign({}, defaultMarkerSeriesOptions), { hide: false, color: chartColors.usdValue }));
                markerUsdSeries.setData(data.usdValueMarkersDataPoints);
            }
        }
        this._fitChart();
        if (this._container) {
            chart.resize((_a = this._container) === null || _a === void 0 ? void 0 : _a.clientWidth, (_b = this._container) === null || _b === void 0 ? void 0 : _b.clientHeight, true);
        }
    }
    setOnSeriesLineSelected(callback) {
        this._onSeriesLineSelected = callback;
    }
    setContainer(value) {
        this._container = value;
        this._chart = createChart(this._container, chartOptions);
        const { plotAreaTooltip, yAxisLeftTooltip, yAxisRightTooltip, xAxisTooltip, } = _makeTooltips(this._container);
        this.addTooltip('plot-area-tooltip', plotAreaTooltip);
        this.addTooltip('y-axis-left-tooltip', yAxisLeftTooltip);
        this.addTooltip('y-axis-right-tooltip', yAxisRightTooltip);
        this.addTooltip('x-axis-tooltip', xAxisTooltip);
        if (this._chart) {
            this._chart.subscribeClick(this._handleClick);
            /**
             * Note: thre are 4 tooltip DOM's that needs to be initiated by now
             * - A tooltip for the left y axis
             * - A tooltip for the right y axis
             * - A tooltip for the x axis
             * - A tooltip on the markers
             *
             * With out these DOM elements which are stored as references in `this._tooltips`
             * we really can't do much with the crosshair move event, hence the check
             */
            if (Object.keys(this._tooltips).length === 4 &&
                !this._crosshairSubscribed) {
                this._chart.subscribeCrosshairMove(this._handleCrosshairMove);
            }
        }
        window.addEventListener('resize', this._onResize);
    }
    getChart() {
        return this._chart;
    }
    addTooltip(key, value) {
        if (this._tooltips[key]) {
            return;
        }
        this._tooltips[key] = value;
    }
    getTooltip(key) {
        return this._tooltips[key] || null;
    }
    addPrimitive(key, value) {
        this._primitives[key] = value;
    }
    addSeries(key, value) {
        if (this._series[key]) {
            return;
        }
        this._series[key] = value;
    }
    getSeries(key) {
        return this._series[key] || null;
    }
    createSeries(key, generateSeries) {
        let series = this.getSeries(key);
        if (!series) {
            series = generateSeries();
            this.addSeries(key, series);
        }
    }
    emptyObject(obj) {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                delete obj[key];
            }
        }
    }
    flush() {
        var _a, _b, _c;
        this.emptyObject(this._series);
        this.emptyObject(this._tooltips);
        (_a = this._chart) === null || _a === void 0 ? void 0 : _a.unsubscribeCrosshairMove(this._handleCrosshairMove);
        this._crosshairSubscribed = false;
        (_b = this._chart) === null || _b === void 0 ? void 0 : _b.unsubscribeClick(this._handleClick);
        (_c = this._chart) === null || _c === void 0 ? void 0 : _c.remove();
        this._chart = null;
        this._data = null;
        this._selectedSeries = null;
        this._container = null;
        window.removeEventListener('resize', this._onResize);
    }
}
export const chartManager = new ChartManager();
